import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import ExecDashboardStore from 'stores/ExecDashboardStore';
import ExecDashboardActions from 'actions/ExecDashboardActions';
import {List} from 'immutable';
import moment from 'moment';
import {extent} from 'd3-array';
import TrendChart from 'components/pure/charts/trend/TrendChart';
import {TRENDLINE_COLORS} from 'components/manager-dashboard/Common';
import UserStore from 'stores/UserStore';
import {BEST_AND_WORST, groupings, trendMetrics} from 'components/pure/charts/trend';
import {GroupingTrendChartLegend} from 'components/pure/charts/trend/GroupingTrendChartLegend';

class ExecTrendChartContainer extends React.Component {
  static getStores() {
    return [ExecDashboardStore, UserStore];
  }

  static getPropsFromStores() {
    const store = ExecDashboardStore.getState();
    const userStore = UserStore.getState();
    return {
      trendData: store.trendData,
      trendDataLoading: store.trendDataLoading,
      users: userStore.users,
      usersLoading: userStore.usersLoading,
      userListLoading: userStore.userListLoading,
    };
  }

  tooltipContent(item, d, index, pins) {
    if (pins.includes(index) || pins.length === 0) {
      return (
        <div className="saw-chart-tooltip">
          <p>Date: {d.date.format('M/D/YY')}</p>
          <p>Value: {this.props.formatValue(d.value)}</p>
        </div>
      );
    }
    return null;
  }

  renderChart(loading, yRange, trends) {
    const {chartLabel} = this.props;
    const metrics = trendMetrics(List(trends));
    return (
      <TrendChart
        yRange={yRange}
        yLabel={''} // no label, they're all different and context was implied
        margins={{
          top: 10,
          left: 50,
          right: 15,
          bottom: 25,
        }}
        trends={trends}
        legend={GroupingTrendChartLegend}
        loading={loading}
        onClose={this.props.onClose}
        tooltipContent={this.tooltipContent.bind(this)}
        label={chartLabel}
        formatValue={this.props.formatValue}
        trendMetrics={metrics}
        prefilteredTrends={List(groupings()[BEST_AND_WORST](metrics))}
        preselectedGroup={BEST_AND_WORST}
      />
    );
  }

  render() {
    const {trendDataLoading, userListLoading, usersLoading} = this.props;
    // TODO figure out why some users are still not available even when loading flags are false.
    const loading =
      trendDataLoading || userListLoading || usersLoading.valueSeq().filter(t => t).size > 0;
    if (loading) {
      return this.renderChart(true, extent([0, 100]), List());
    } else {
      const {teamId, trendData, users} = this.props;
      const trendsMassaged = trendData
        ? trendData
            .entrySeq()
            .map(([k, v]) => ({
              userId: k,
              data: List(
                v
                  .get('data')
                  .toJS()
                  .map(d => ({...d, date: moment(d.date)}))
              ),
            }))
            .toList()
        : List();

      const colors = TRENDLINE_COLORS;

      const trends = trendsMassaged.map((t, i) => {
        return {
          color: colors[i % colors.length],
          // preserve these so we can easily get the entire date range
          firstDate: t.data.size > 0 ? t.data.first().date : moment().subtract(7, 'days'),
          lastDate: t.data.size > 0 ? t.data.last().date : moment(),
          user: users.get(t.userId),
          data: t.data.filter(d => d.value !== null),
        };
      });

      const filterByTeam = i => {
        return teamId
          ? // teamId will not always be set (e.g., topics rollups don't always set it). Avoid
            // filtering if it's not set.
            i.user && i.user.teamId === teamId
          : true;
      };

      const trendsToDisplay = trends
        // users may still be loading -- loading state isn't reliable here :(
        .filter(i => i.user)
        .filter(filterByTeam)
        .map(i => ({...i, label: i.user.getFullName()}));

      const yRange = trendData
        ? extent([0, ...trendsToDisplay.flatMap(d => d.data).map(d => d.value)])
        : extent([0, 100]);

      return this.renderChart(false, yRange, trendsToDisplay);
    }
  }

  componentWillUnmount() {
    ExecDashboardActions.clearTrendData();
  }
}

export default connectToStores(ExecTrendChartContainer);
